import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, X, CalendarClock } from "lucide-react";
import { api, formatApiError, brl } from "@/lib/api";
import { prettyDate } from "@/lib/dates";

const STATUS = {
  pendente: { l: "Pendente", c: "bg-amber-100 text-amber-800" },
  confirmada: { l: "Confirmada", c: "bg-emerald-100 text-emerald-800" },
  cancelada: { l: "Cancelada", c: "bg-red-100 text-red-700" },
  expirada: { l: "Expirada", c: "bg-slate-200 text-slate-500" },
  creditada: { l: "Creditada ⭐", c: "bg-amber-100 text-amber-800" },
};

export default function MyReservationsDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    if (q.trim().length < 3) {
      toast.error("Digite ao menos 3 letras do nome, @ ou telefone.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/my-reservations", { params: { q: q.trim() } });
      setResults(res.data);
      setSearched(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/reservations/${id}/cancel-public`);
      toast.success("Reserva cancelada.");
      search();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="my-reservations-dialog"
        className="max-w-lg gap-0 overflow-hidden rounded-3xl border-0 bg-white p-0 shadow-2xl"
      >
        <DialogTitle className="sr-only">Minhas reservas</DialogTitle>
        <DialogDescription className="sr-only">
          Consulte e cancele suas reservas pelo nome, Instagram ou telefone.
        </DialogDescription>

        <div className="bg-[#122018] px-6 py-5">
          <h3 className="font-display text-2xl font-bold text-white">Minhas reservas</h3>
          <p className="mt-1 text-sm text-emerald-100/80">
            Busque pelo seu nome, @ do Instagram ou telefone.
          </p>
          <div className="mt-3 flex gap-2">
            <Input
              data-testid="my-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Ex.: João, @joao ou 48 9..."
              className="rounded-xl border-0 bg-white/95"
            />
            <Button
              data-testid="my-search-btn"
              onClick={search}
              disabled={loading}
              className="rounded-xl bg-[#E8611A] px-5 font-semibold text-white hover:bg-[#CF5212]"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
          ) : !searched ? (
            <p className="py-8 text-center text-sm text-slate-400">
              Digite seus dados e toque em buscar.
            </p>
          ) : results.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">
              Nenhuma reserva encontrada para essa busca.
            </p>
          ) : (
            results.map((r) => {
              const st = STATUS[r.status] || { l: r.status, c: "bg-slate-100 text-slate-600" };
              const canCancel = r.status === "pendente" || r.status === "confirmada";
              return (
                <div
                  key={r.id}
                  data-testid={`my-reservation-${r.id}`}
                  className="rounded-2xl border border-slate-200 p-4"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display text-lg font-bold text-slate-900">
                      {r.space_name}
                    </span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.c}`}>
                      {st.l}
                    </span>
                  </div>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                    <CalendarClock className="h-4 w-4" /> {prettyDate(r.date)} · {r.time_label}
                  </p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-bold text-[#E8611A]">{brl(r.price)}</span>
                    {canCancel && (
                      <Button
                        data-testid={`my-cancel-${r.id}`}
                        onClick={() => cancel(r.id)}
                        size="sm"
                        variant="outline"
                        className="rounded-full border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                      >
                        <X className="mr-1.5 h-4 w-4" /> Cancelar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
